import {
    TActionType,
    TransactionType,
} from '../../Utility/Tracking/DataSet/Transaction';
import { track, TrackMethod } from '../../Utility/Tracking/Track';
import { trackTransaction } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { IGameState } from '../Game/IGameState';
import { LevelType } from '../Level/ILevelState';
import { MissionType } from './IMission';
import { ILevelData, IPlayerState, UnlockType } from './IPlayerState';
import { unlockBackCard } from './PlayerBackcardHelper';
import { setCoins, setLives } from './PlayerInventoryHelper';
import { updateMissionProgress } from './PlayerMissionHelper';
import { updateGateTimer } from './PlayerTimerHelper';

export function getNewLevelData(
    levelData: { levelNumber: number } & Partial<ILevelData>
): ILevelData {
    return {
        levelNumber: levelData.levelNumber,
        stars: levelData.stars || 0,
        attempts: levelData.attempts || 0,
        helperFriendAttempts: levelData.helperFriendAttempts || 0,
    };
}

export function getLevelData(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType
): { state: IGameState; level: ILevelData } {
    const level = state.playerState.levels[levelType].find(
        levelData => levelData.levelNumber === levelNumber
    );

    if (level !== undefined) {
        return { state, level };
    }

    const newLevel = getNewLevelData({
        levelNumber: levelNumber,
    });

    return {
        state: updateLevel(state, levelNumber, levelType, newLevel),
        level: newLevel,
    };
}

/**
 * Upsert a level into the player state.
 * @param state
 * @param levelNumber Required, unique level number to insert or update.
 * @param levelData Partial level data set to upsert with.
 */
export function updateLevel(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType,
    levelData: Partial<ILevelData>
): IGameState {
    // If the level is not found, add it.
    if (
        state.playerState.levels[levelType].find(
            level => level.levelNumber === levelNumber
        ) === undefined
    ) {
        return setLevels(state, levelType, [
            ...state.playerState.levels[levelType],
            {
                ...getNewLevelData({ levelNumber, ...levelData }),
                levelNumber, // Level number appended at the end just to be sure.
            },
        ]);
    }

    // If the level exists, update it.
    return setLevels(
        state,
        levelType,
        state.playerState.levels[levelType].map(level => {
            if (level.levelNumber !== levelNumber) return level;
            return {
                ...level,
                ...levelData,
                levelNumber, // Level number appended at the end just to be sure.
            };
        })
    );
}

export function setLevels(
    state: IGameState,
    levelType: LevelType,
    levels: ILevelData[]
) {
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            levels: {
                ...state.playerState.levels,
                [levelType]: levels,
            },
        },
    };

    state.playerState = autoUnlockGates(state.playerState);

    state.playerState = autoUnlockSuitcase(state.playerState);

    // Update gate timer in case this is the last level of the world.
    state.playerState = updateGateTimer(state.playerState);

    // TODO: Move following section to own method.
    // Unlock star backcard.
    const totalStars = getLevelStarsTotal(state.playerState);
    if (totalStars >= 30) {
        state = unlockBackCard(state, 4, false);
    }
    if (totalStars >= 75) {
        state = unlockBackCard(state, 5, false);
    }
    if (totalStars >= 115) {
        state = unlockBackCard(state, 6, false);
    }

    return state;
}

export function levelStart(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType
): IGameState {
    const { state: newState, level } = getLevelData(
        state,
        levelNumber,
        levelType
    );

    state = newState;

    state = setLevelAttempts(state, levelNumber, levelType, level.attempts + 1);

    state = setHelperFriendAttempts(
        state,
        levelNumber,
        levelType,
        level.helperFriendAttempts + 1
    );

    state = setLevelId(state, levelNumber, levelType);

    track('mLevelTypeContext', levelType);

    if (levelType === LevelType.SAGA) {
        return {
            ...state,
            playerState: setLives(
                state.playerState,
                state.playerState.lives - 1
            ),
        };
    } else {
        return {
            ...state,
            // TODO: Spend a ticket.
        };
    }
}

export function levelComplete(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType,
    stars: number,
    coins: number
): IGameState {
    state = {
        ...state,
        playerState: {
            ...state.playerState,
        },
    };

    // Get the existing level to compare star counts.
    const levels = state.playerState.levels[levelType];
    const oldLevel = levels.find(level => level.levelNumber === levelNumber);

    // If previous star value was zero this is the first time the player has completed this level.
    // Update any level completion missions and grant coin reward.
    if (oldLevel.stars === 0) {
        state.playerState = updateMissionProgress(
            state.playerState,
            MissionType.WIN_LEVEL,
            1
        );
        state.playerState = setCoins(
            state.playerState,
            state.playerState.coins + coins
        );
        track('tCoinsLevelEnd', coins, TrackMethod.ADD);
        trackTransaction(state, TransactionType.REWARD, TActionType.REWARD);
    }

    // If the star count has increased then update any star missions.
    if (oldLevel.stars < stars) {
        state.playerState = updateMissionProgress(
            state.playerState,
            MissionType.COLLECT_STARS,
            stars - oldLevel.stars
        );

        state = updateLevel(state, levelNumber, levelType, {
            ...oldLevel,
            stars: stars,
        });
    }

    // Re-add life after completing level.
    state.playerState = setLives(
        state.playerState,
        state.playerState.lives + 1,
        false
    );
    return state;
}

export function setLevelId(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType
): IGameState {
    const levelId =
        levelType === LevelType.CHALLENGE
            ? `challenge_${levelNumber}` // TODO: Could be generated using the challenge ID
            : levelNumber.toString();

    return {
        ...state,
        levelState: {
            ...state.levelState,
            levelId: levelId,
        },
    };
}

export function setLevelAttempts(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType,
    value: number
): IGameState {
    return updateLevel(state, levelNumber, levelType, {
        attempts: value,
    });
}

export function setHelperFriendAttempts(
    state: IGameState,
    levelNumber: number,
    levelType: LevelType,
    value: number
): IGameState {
    return updateLevel(state, levelNumber, levelType, {
        helperFriendAttempts: value,
    });
}

/**
 * Get the total number of stars earned by the player.
 * @param state
 */
export function getLevelStarsTotal(state: IPlayerState): number {
    return state.levels.saga.reduce((a, b) => a + b.stars, 0);
}

/**
 * Return levels from player level data that have star values.
 * @param levelData  the levels in playerstate
 */
export function getCompletedLevels(levelData: ILevelData[]) {
    return levelData.filter(level => level.stars > 0);
}

export function autoUnlockGates(state: IPlayerState): IPlayerState {
    const world = Math.max(
        state.world,
        Math.ceil(getCompletedLevels(state.levels.saga).length / 60)
    );

    return {
        ...state,
        world: world,
        activeWorld: world,
    };
}

/**
 * Auto-unlock suitcase and boosters if player has (somehow --> cheats or restore-lost-progress) progressed
 * beyond the levels in which they are unlocked.
 * @param state
 */
export function autoUnlockSuitcase(state: IPlayerState): IPlayerState {
    const playerUnlocks = state.playerUnlocks.slice();

    const levelCount = getCompletedLevels(state.levels.saga).length;

    // prettier-ignore
    if (levelCount > 26 && !playerUnlocks.find(unlock => unlock.type === UnlockType.SUITCASE)) {
        playerUnlocks.push({ type: UnlockType.SUITCASE });
    }
    // prettier-ignore
    if (levelCount > 26 && !playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_TNT)) {
        playerUnlocks.push({ type: UnlockType.BOOSTER_TNT });
    }
    // prettier-ignore
    if (levelCount > 40 && !playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_BULLS_EYE)) {
        playerUnlocks.push({ type: UnlockType.BOOSTER_BULLS_EYE });
    }
    // prettier-ignore
    if (levelCount > 45 && !playerUnlocks.find(unlock => unlock.type === UnlockType.BOOSTER_SHUFFLE)) {
        playerUnlocks.push({ type: UnlockType.BOOSTER_SHUFFLE});
    }

    return {
        ...state,
        playerUnlocks: playerUnlocks,
    };
}

/**
 * Get the highest SAGA level number. This is the level number after the last level that was completed.
 * @example If you complete level 332 this function will return 333.
 * @param state
 */
export function getCurrentLevelNumber(state: IGameState): number {
    return (
        Math.max(
            0,
            ...getCompletedLevels(state.playerState.levels.saga).map(
                l => l.levelNumber
            ),
            0
        ) + 1
    );
}
